import {
    addManagerTestItem,
    buildManagerSopItemPayload,
    fetchManagerTestItem,
    fetchManagerTestItemsList,
    parseManagerTestItemAddResponse,
    updateManagerTestItem,
    updateManagerTestItemStatus,
} from 'shared/api/managerTestItem';
import { fetchTestItem, TEST_MANAGER_ITEM_STATUS_ARCHIVED, TEST_MANAGER_ITEM_STATUS_PUBLISHED } from 'shared/api/testItem';
import { extractManagerTestItemsListArray } from 'shared/pages/testsAdmin/mapManagerTestItemsList';
import { mockStore } from './mockStore';
import { hasSopLmsFields, isSopListRow, listRowNeedsSopDetail, mapSopItemToCampaign } from './mapSopItem';
import {
    TeachingEvaluationCampaign,
    TeachingEvaluationCampaignInput,
    TeachingEvaluationStatus,
    formatToLmsVoteFormatType,
    votingModeToLmsVoteType,
} from './types';

const delay = <T,>(value: T, ms = 120): Promise<T> =>
    new Promise((resolve) => setTimeout(() => resolve(value), ms));

const emptyCampaign = (teamId: string): TeachingEvaluationCampaign => ({
    id: '',
    teamId,
    name: '',
    moduleId: '',
    moduleName: '',
    format: 'module',
    votingMode: 'general',
    groupIds: [],
    customVoteGroups: [],
    teacherIds: [],
    teacherNames: [],
    openAt: '',
    closeAt: '',
    status: 'draft',
    createdAt: '',
    updatedAt: '',
});

const toSopPayload = (input: TeachingEvaluationCampaignInput) =>
    buildManagerSopItemPayload({
        entityId: input.moduleIds?.[0] || input.moduleId,
        name: input.name,
        startDate: input.openAt || null,
        endDate: input.closeAt || null,
        lmsModuleId: input.moduleIds?.[0] || input.moduleId,
        lmsModuleIds: input.moduleIds?.length
            ? input.moduleIds
            : input.moduleId
              ? [input.moduleId]
              : [],
        lmsVoteFormatType: formatToLmsVoteFormatType(input.format),
        lmsVoteType: votingModeToLmsVoteType(input.votingMode),
        lmsVoteGroupIds: input.groupIds,
        lmsCustomVoteGroups: input.customVoteGroups,
        status: 0,
    });

const shouldPublish = (status?: TeachingEvaluationStatus) =>
    Boolean(status && status !== 'draft');

const publishSopItem = (id: string) =>
    updateManagerTestItemStatus({
        id,
        status: TEST_MANAGER_ITEM_STATUS_PUBLISHED,
    });

const campaignFromInput = (
    input: TeachingEvaluationCampaignInput,
    id: string,
    prev?: TeachingEvaluationCampaign | null,
): TeachingEvaluationCampaign => {
    const now = new Date().toISOString();
    return {
        ...input,
        id,
        groupIds: input.groupIds || [],
        customVoteGroups: input.customVoteGroups || [],
        teacherIds: input.teacherIds || [],
        teacherNames: input.teacherNames || [],
        status: input.status ?? prev?.status ?? 'draft',
        createdAt: prev?.createdAt || now,
        updatedAt: now,
    };
};

const fetchSopItem = async (id: string): Promise<unknown> => {
    const managerRaw = await fetchManagerTestItem(id);
    if (hasSopLmsFields(managerRaw)) return managerRaw;
    try {
        const publicRaw = await fetchTestItem(id);
        if (hasSopLmsFields(publicRaw)) return publicRaw;
    } catch {
    }
    return managerRaw;
};

export const teachingEvaluationApi = {
    async list(teamId: string): Promise<TeachingEvaluationCampaign[]> {
        const raw = await fetchManagerTestItemsList({
            courseId: teamId,
            statuses: [0, 1],
        });
        const rows = extractManagerTestItemsListArray(raw).filter(isSopListRow);
        const campaigns = await Promise.all(
            rows.map(async (row) => {
                const fromList = mapSopItemToCampaign(row, emptyCampaign(teamId));
                if (!fromList?.id) return null;
                let mapped = fromList;
                if (listRowNeedsSopDetail(row) && !fromList.lmsInfo?.modules?.length) {
                    try {
                        const detail = await fetchSopItem(mapped.id);
                        mapped = mapSopItemToCampaign(detail, mapped) ?? mapped;
                    } catch {
                        // keep list row
                    }
                }
                if (fromList.lmsInfo) {
                    mapped = { ...mapped, lmsInfo: fromList.lmsInfo };
                }
                const withTeam = { ...mapped, teamId: mapped.teamId || teamId };
                mockStore.upsert(withTeam);
                return withTeam;
            }),
        );
        return campaigns.filter(
            (item): item is TeachingEvaluationCampaign => Boolean(item),
        );
    },

    async get(id: string): Promise<TeachingEvaluationCampaign | null> {
        const local = mockStore.get(id);
        try {
            const raw = await fetchSopItem(id);
            const mapped = mapSopItemToCampaign(raw, local);
            if (mapped) {
                mockStore.upsert({ ...mapped, teamId: mapped.teamId || local?.teamId || '' });
                return mapped;
            }
        } catch {
        }
        return delay(local);
    },

    async create(input: TeachingEvaluationCampaignInput): Promise<TeachingEvaluationCampaign> {
        const raw = await addManagerTestItem(toSopPayload(input));
        const id = parseManagerTestItemAddResponse(raw);
        if (!id) {
            throw new Error('SOP create: missing testItemId');
        }
        if (shouldPublish(input.status)) {
            await publishSopItem(id);
        }
        const campaign = campaignFromInput(input, id);
        return mockStore.upsert(campaign);
    },

    async update(
        id: string,
        input: Partial<TeachingEvaluationCampaignInput> & { status?: TeachingEvaluationStatus },
    ): Promise<TeachingEvaluationCampaign | null> {
        const prev = mockStore.get(id);
        const merged: TeachingEvaluationCampaignInput = {
            teamId: input.teamId ?? prev?.teamId ?? '',
            name: input.name ?? prev?.name ?? '',
            moduleId: input.moduleId ?? input.moduleIds?.[0] ?? prev?.moduleId ?? '',
            moduleName: input.moduleName ?? prev?.moduleName ?? '',
            moduleIds: input.moduleIds ?? prev?.moduleIds,
            format: input.format ?? prev?.format ?? 'module',
            votingMode: input.votingMode ?? prev?.votingMode ?? 'general',
            groupIds: input.groupIds ?? prev?.groupIds ?? [],
            customVoteGroups: input.customVoteGroups ?? prev?.customVoteGroups ?? [],
            teacherIds: input.teacherIds ?? prev?.teacherIds ?? [],
            teacherNames: input.teacherNames ?? prev?.teacherNames ?? [],
            teacherAvatarUrls: input.teacherAvatarUrls ?? prev?.teacherAvatarUrls,
            openAt: input.openAt ?? prev?.openAt ?? '',
            closeAt: input.closeAt ?? prev?.closeAt ?? '',
            status: input.status ?? prev?.status,
            lmsInfo: input.lmsInfo ?? prev?.lmsInfo,
        };
        await updateManagerTestItem({
            id,
            ...toSopPayload(merged),
        });
        if (shouldPublish(merged.status)) {
            await publishSopItem(id);
        }
        const campaign = campaignFromInput(merged, id, prev);
        return mockStore.upsert(campaign);
    },

    async remove(id: string): Promise<boolean> {
        await updateManagerTestItemStatus({
            id,
            status: TEST_MANAGER_ITEM_STATUS_ARCHIVED,
        });
        return true;
    },

    hideLocal(id: string): Promise<boolean> {
        return delay(mockStore.hideLocal(id));
    },

    cancel(id: string): Promise<TeachingEvaluationCampaign | null> {
        return delay(mockStore.cancel(id));
    },

    getActiveForModule(
        teamId: string,
        moduleId: string,
    ): Promise<TeachingEvaluationCampaign | null> {
        return delay(mockStore.getActiveForModule(teamId, moduleId));
    },

    isCompleted(campaignId: string, accountId: string, lmsModuleId?: string): Promise<boolean> {
        return delay(mockStore.isCompleted(campaignId, accountId, lmsModuleId));
    },

    markCompleted(campaignId: string, accountId: string, lmsModuleId?: string): Promise<void> {
        mockStore.markCompleted(campaignId, accountId, lmsModuleId);
        if (typeof window !== 'undefined') {
            window.dispatchEvent(new CustomEvent('teaching-evaluation-completed', { detail: { campaignId, lmsModuleId } }));
        }
        return delay(undefined);
    },
};
import {
    addManagerTestItem,
    buildManagerSopItemPayload,
    fetchManagerTestItem,
    fetchManagerTestItemsList,
    parseManagerTestItemAddResponse,
    updateManagerTestItem,
    updateManagerTestItemStatus,
} from 'shared/api/managerTestItem';
import { fetchTestItem, TEST_MANAGER_ITEM_STATUS_ARCHIVED, TEST_MANAGER_ITEM_STATUS_PUBLISHED } from 'shared/api/testItem';
import { extractManagerTestItemsListArray } from 'shared/pages/testsAdmin/mapManagerTestItemsList';
import { mockStore } from './mockStore';
import { hasSopLmsFields, isSopListRow, listRowNeedsSopDetail, mapSopItemToCampaign } from './mapSopItem';
import {
    TeachingEvaluationCampaign,
    TeachingEvaluationCampaignInput,
    TeachingEvaluationStatus,
    formatToLmsVoteFormatType,
    votingModeToLmsVoteType,
} from './types';

const delay = <T,>(value: T, ms = 120): Promise<T> =>
    new Promise((resolve) => setTimeout(() => resolve(value), ms));

const emptyCampaign = (teamId: string): TeachingEvaluationCampaign => ({
    id: '',
    teamId,
    name: '',
    moduleId: '',
    moduleName: '',
    format: 'module',
    votingMode: 'general',
    groupIds: [],
    customVoteGroups: [],
    teacherIds: [],
    teacherNames: [],
    openAt: '',
    closeAt: '',
    status: 'draft',
    createdAt: '',
    updatedAt: '',
});

const toSopPayload = (input: TeachingEvaluationCampaignInput) =>
    buildManagerSopItemPayload({
        entityId: input.moduleIds?.[0] || input.moduleId,
        name: input.name,
        startDate: input.openAt || null,
        endDate: input.closeAt || null,
        lmsModuleId: input.moduleIds?.[0] || input.moduleId,
        lmsModuleIds: input.moduleIds?.length
            ? input.moduleIds
            : input.moduleId
              ? [input.moduleId]
              : [],
        lmsVoteFormatType: formatToLmsVoteFormatType(input.format),
        lmsVoteType: votingModeToLmsVoteType(input.votingMode),
        lmsVoteGroupIds: input.groupIds,
        lmsCustomVoteGroups: input.customVoteGroups,
        status: 0,
    });

const shouldPublish = (status?: TeachingEvaluationStatus) =>
    Boolean(status && status !== 'draft');

const publishSopItem = (id: string) =>
    updateManagerTestItemStatus({
        id,
        status: TEST_MANAGER_ITEM_STATUS_PUBLISHED,
    });

const campaignFromInput = (
    input: TeachingEvaluationCampaignInput,
    id: string,
    prev?: TeachingEvaluationCampaign | null,
): TeachingEvaluationCampaign => {
    const now = new Date().toISOString();
    return {
        ...input,
        id,
        groupIds: input.groupIds || [],
        customVoteGroups: input.customVoteGroups || [],
        teacherIds: input.teacherIds || [],
        teacherNames: input.teacherNames || [],
        status: input.status ?? prev?.status ?? 'draft',
        createdAt: prev?.createdAt || now,
        updatedAt: now,
    };
};

const fetchSopItem = async (id: string): Promise<unknown> => {
    const managerRaw = await fetchManagerTestItem(id);
    if (hasSopLmsFields(managerRaw)) return managerRaw;
    try {
        const publicRaw = await fetchTestItem(id);
        if (hasSopLmsFields(publicRaw)) return publicRaw;
    } catch {
    }
    return managerRaw;
};

export const teachingEvaluationApi = {
    async list(teamId: string): Promise<TeachingEvaluationCampaign[]> {
        const raw = await fetchManagerTestItemsList({
            courseId: teamId,
            statuses: [0, 1],
        });
        const rows = extractManagerTestItemsListArray(raw).filter(isSopListRow);
        const campaigns = await Promise.all(
            rows.map(async (row) => {
                const fromList = mapSopItemToCampaign(row, emptyCampaign(teamId));
                if (!fromList?.id) return null;
                let mapped = fromList;
                if (listRowNeedsSopDetail(row) && !fromList.lmsInfo?.modules?.length) {
                    try {
                        const detail = await fetchSopItem(mapped.id);
                        mapped = mapSopItemToCampaign(detail, mapped) ?? mapped;
                    } catch {
                        // keep list row
                    }
                }
                if (fromList.lmsInfo) {
                    mapped = { ...mapped, lmsInfo: fromList.lmsInfo };
                }
                const withTeam = { ...mapped, teamId: mapped.teamId || teamId };
                mockStore.upsert(withTeam);
                return withTeam;
            }),
        );
        return campaigns.filter(
            (item): item is TeachingEvaluationCampaign => Boolean(item),
        );
    },

    async get(id: string): Promise<TeachingEvaluationCampaign | null> {
        const local = mockStore.get(id);
        try {
            const raw = await fetchSopItem(id);
            const mapped = mapSopItemToCampaign(raw, local);
            if (mapped) {
                mockStore.upsert({ ...mapped, teamId: mapped.teamId || local?.teamId || '' });
                return mapped;
            }
        } catch {
        }
        return delay(local);
    },

    async create(input: TeachingEvaluationCampaignInput): Promise<TeachingEvaluationCampaign> {
        const raw = await addManagerTestItem(toSopPayload(input));
        const id = parseManagerTestItemAddResponse(raw);
        if (!id) {
            throw new Error('SOP create: missing testItemId');
        }
        if (shouldPublish(input.status)) {
            await publishSopItem(id);
        }
        const campaign = campaignFromInput(input, id);
        return mockStore.upsert(campaign);
    },

    async update(
        id: string,
        input: Partial<TeachingEvaluationCampaignInput> & { status?: TeachingEvaluationStatus },
    ): Promise<TeachingEvaluationCampaign | null> {
        const prev = mockStore.get(id);
        const merged: TeachingEvaluationCampaignInput = {
            teamId: input.teamId ?? prev?.teamId ?? '',
            name: input.name ?? prev?.name ?? '',
            moduleId: input.moduleId ?? input.moduleIds?.[0] ?? prev?.moduleId ?? '',
            moduleName: input.moduleName ?? prev?.moduleName ?? '',
            moduleIds: input.moduleIds ?? prev?.moduleIds,
            format: input.format ?? prev?.format ?? 'module',
            votingMode: input.votingMode ?? prev?.votingMode ?? 'general',
            groupIds: input.groupIds ?? prev?.groupIds ?? [],
            customVoteGroups: input.customVoteGroups ?? prev?.customVoteGroups ?? [],
            teacherIds: input.teacherIds ?? prev?.teacherIds ?? [],
            teacherNames: input.teacherNames ?? prev?.teacherNames ?? [],
            teacherAvatarUrls: input.teacherAvatarUrls ?? prev?.teacherAvatarUrls,
            openAt: input.openAt ?? prev?.openAt ?? '',
            closeAt: input.closeAt ?? prev?.closeAt ?? '',
            status: input.status ?? prev?.status,
            lmsInfo: input.lmsInfo ?? prev?.lmsInfo,
        };
        await updateManagerTestItem({
            id,
            ...toSopPayload(merged),
        });
        if (shouldPublish(merged.status)) {
            await publishSopItem(id);
        }
        const campaign = campaignFromInput(merged, id, prev);
        return mockStore.upsert(campaign);
    },

    async remove(id: string): Promise<boolean> {
        await updateManagerTestItemStatus({
            id,
            status: TEST_MANAGER_ITEM_STATUS_ARCHIVED,
        });
        return true;
    },

    hideLocal(id: string): Promise<boolean> {
        return delay(mockStore.hideLocal(id));
    },

    cancel(id: string): Promise<TeachingEvaluationCampaign | null> {
        return delay(mockStore.cancel(id));
    },

    getActiveForModule(
        teamId: string,
        moduleId: string,
    ): Promise<TeachingEvaluationCampaign | null> {
        return delay(mockStore.getActiveForModule(teamId, moduleId));
    },

    isCompleted(campaignId: string, accountId: string, lmsModuleId?: string): Promise<boolean> {
        return delay(mockStore.isCompleted(campaignId, accountId, lmsModuleId));
    },

    markCompleted(campaignId: string, accountId: string, lmsModuleId?: string): Promise<void> {
        mockStore.markCompleted(campaignId, accountId, lmsModuleId);
        if (typeof window !== 'undefined') {
            window.dispatchEvent(new CustomEvent('teaching-evaluation-completed', { detail: { campaignId, lmsModuleId } }));
        }
        return delay(undefined);
    },
};
